import type { ParsedInsightsFilters } from '@/common/api/types/dashboard/filters';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import type { DateOperator } from '@/common/zod-schemas/common/operators';

interface BuildItemFilterParams extends ParsedInsightsFilters {
  scopeItemId?: number;
  loopId: number;
}

/**
 * Builds common filters shared by all endpoints (team, plan, status, dates, etc.).
 * Does NOT include container-specific filters (parentId, ancestorId, id).
 *
 * @param params - Parsed filter values from UI
 * @returns ItemFiltersType with common filters only
 */
const buildDateFilter = (
  operator: DateOperator,
  value: string[] | null,
): { value: string[] | null; operator: DateOperator } | undefined => {
  if (operator === 'null' || operator === 'notnull') {
    return { value: null, operator };
  }

  if (operator === 'eq' && value?.length === 1) {
    const dateStr = value[0];
    if (!dateStr) {
      return { value, operator };
    }

    const startDate = new Date(dateStr);
    const endDate = new Date(startDate);
    endDate.setUTCDate(startDate.getUTCDate() + 1);

    return {
      value: [startDate.toISOString(), endDate.toISOString()],
      operator: 'in',
    };
  }

  return { value, operator };
};

export const buildCommonFilters = (params: ParsedInsightsFilters): ItemFiltersType => {
  const filters: ItemFiltersType = {};

  if (params.teamId?.length) filters.teamId = { value: params.teamId, operator: 'in' };
  if (params.statusId?.length) filters.statusId = { value: params.statusId, operator: 'in' };
  if (params.itemTypeId?.length) filters.itemTypeId = { value: params.itemTypeId, operator: 'in' };

  if (params.plannedStartDateTime) {
    const dateFilter = buildDateFilter(
      params.plannedStartDateTime.operator,
      params.plannedStartDateTime.value,
    );
    if (dateFilter) {
      filters.plannedStartDateTime = {
        value: dateFilter.value,
        operator: dateFilter.operator,
      };
    }
  }
  if (params.plannedFinishDateTime) {
    const dateFilter = buildDateFilter(
      params.plannedFinishDateTime.operator,
      params.plannedFinishDateTime.value,
    );
    if (dateFilter) {
      filters.plannedFinishDateTime = {
        value: dateFilter.value,
        operator: dateFilter.operator,
      };
    }
  }

  return filters;
};

/**
 * Builds filter for items endpoint (GET /api/v1/items).
 * Uses parentId to fetch direct children of a scope item.
 * Applies "descendant." prefix to all common filters to query across item hierarchy.
 *
 * @param params - Parsed filter values and optional scopeItemId
 * @returns ItemFiltersType for items endpoint with descendant-prefixed filters
 */
export const buildItemsFilter = (params: BuildItemFilterParams): ItemFiltersType => {
  const commonFilters = buildCommonFilters(params);
  const filters: ItemFiltersType = {};

  if (commonFilters.teamId) filters['descendant.teamId'] = commonFilters.teamId;
  if (commonFilters.statusId) filters['descendant.statusId'] = commonFilters.statusId;
  if (commonFilters.itemTypeId) filters['descendant.itemTypeId'] = commonFilters.itemTypeId;

  // Date filters are applied directly without descendant prefix
  if (commonFilters.plannedStartDateTime) {
    filters.plannedStartDateTime = commonFilters.plannedStartDateTime;
  }
  if (commonFilters.plannedFinishDateTime) {
    filters.plannedFinishDateTime = commonFilters.plannedFinishDateTime;
  }

  filters.loopId = { value: params.loopId, operator: 'eq' };

  if (params.scopeItemId) {
    filters.parentId = { value: params.scopeItemId, operator: 'eq' };
  } else {
    filters.parentId = { value: null, operator: 'null' };
  }

  return filters;
};
